/**
 * Subscription Manager
 *
 * Routes messages to subscribers by topic with wildcard support.
 * Topics: 'form.open', 'form.*' matches all form events
 * Fires observer events for subscriber delivery tracking.
 */

import type { ISubscription, ISubscriptionManager, SubscriberCallback } from '../types';
import type { IEnvelope } from '../types';
import type { IPipelineObserver } from '../observability/IPipelineObserver';
import { generatePrefixedId } from '../utils';
import { createModuleLogger } from '../utils';

const logger = createModuleLogger('SubscriptionManager');

class Subscription implements ISubscription {
  readonly id: string;

  active = true;

  constructor(id: string, private unsubscribeFn: () => void) {
    this.id = id;
  }

  unsubscribe(): void {
    this.active = false;
    this.unsubscribeFn();
  }
}

interface SubscriberEntry {
  id: string;
  topic: string;
  callback: SubscriberCallback;
}

export class SubscriptionManager implements ISubscriptionManager {
  private subscribers: Map<string, SubscriberEntry> = new Map();

  private _subscriptions: Subscription[] = [];

  private observer?: IPipelineObserver;

  subscribe<TBody = unknown>(
    topic: string,
    callback: SubscriberCallback<TBody>,
  ): ISubscription {
    const id = generatePrefixedId('sub');
    const entry: SubscriberEntry = { id, topic, callback: callback as SubscriberCallback };
    this.subscribers.set(id, entry);

    const sub = new Subscription(id, () => {
      this.subscribers.delete(id);
    });
    this._subscriptions.push(sub);
    return sub;
  }

  /**
   * Set the observer for subscriber delivery events.
   */
  setObserver(observer: IPipelineObserver): void {
    this.observer = observer;
  }

  async publish<TBody = unknown>(
    topic: string,
    envelope: IEnvelope<TBody>,
    messageId?: string,
  ): Promise<void> {
    const promises: Promise<void>[] = [];

    for (const entry of this.subscribers.values()) {
      if (this.topicMatches(entry.topic, topic)) {
        const start = Date.now();

        // Fire subscriber queued event
        if (messageId && this.observer) {
          try {
            this.observer.onSubscriberQueued({
              messageId,
              subscriberId: entry.id,
              topic: entry.topic,
              timestamp: new Date().toISOString(),
              envelopeSnapshot: envelope, // already provided, no clone needed per event contract
            });
          } catch (error) {
            logger.warn(`[SubscriptionManager] onSubscriberQueued error: ${error}`);
          }
        }

        try {
          const result = entry.callback(envelope);
          const processingTime = Date.now() - start;

          if (result instanceof Promise) {
            promises.push(
              result
                .then(() => {
                  // Fire subscriber delivered event
                  if (messageId && this.observer) {
                    try {
                      this.observer.onSubscriberDelivered({
                        messageId,
                        subscriberId: entry.id,
                        topic: entry.topic,
                        processingTime,
                        callbackName: entry.callback.name || 'anonymous',
                        timestamp: new Date().toISOString(),
                      });
                    } catch (error) {
                      logger.warn(`[SubscriptionManager] onSubscriberDelivered error: ${error}`);
                    }
                  }
                })
                .catch((error) => {
                  const processingTimeTotal = Date.now() - start;
                  // Fire subscriber failed event
                  if (messageId && this.observer) {
                    try {
                      this.observer.onSubscriberFailed({
                        messageId,
                        subscriberId: entry.id,
                        topic: entry.topic,
                        error: error instanceof Error ? error : new Error(String(error)),
                        processingTime: processingTimeTotal,
                        timestamp: new Date().toISOString(),
                      });
                    } catch (observerError) {
                      logger.warn(`[SubscriptionManager] onSubscriberFailed error: ${observerError}`);
                    }
                  }

                  // Log but don't rethrow - other subscribers must continue
                  logger.warn(`Async subscriber error: ${error instanceof Error ? error.message : String(error)}`);
                }),
            );
          } else {
            // Fire subscriber delivered event immediately for sync callbacks
            if (messageId && this.observer) {
              try {
                this.observer.onSubscriberDelivered({
                  messageId,
                  subscriberId: entry.id,
                  topic: entry.topic,
                  processingTime,
                  callbackName: entry.callback.name || 'anonymous',
                  timestamp: new Date().toISOString(),
                });
              } catch (error) {
                logger.warn(`[SubscriptionManager] onSubscriberDelivered error: ${error}`);
              }
            }
          }
        } catch (error) {
          const processingTime = Date.now() - start;

          // Fire subscriber failed event
          if (messageId && this.observer) {
            try {
              this.observer.onSubscriberFailed({
                messageId,
                subscriberId: entry.id,
                topic: entry.topic,
                error: error instanceof Error ? error : new Error(String(error)),
                processingTime,
                timestamp: new Date().toISOString(),
              });
            } catch (observerError) {
              logger.warn(`[SubscriptionManager] onSubscriberFailed error: ${observerError}`);
            }
          }

          // Log but don't break other subscribers
          logger.warn(`Subscriber error: ${error instanceof Error ? error.message : String(error)}`);
        }
      }
    }

    await Promise.all(promises);
  }

  private topicMatches(pattern: string, actual: string): boolean {
    // Exact match
    if (pattern === actual) {
      return true;
    }

    // Wildcard match: 'form.*' matches 'form.open', 'form.close', etc.
    if (pattern.endsWith('.*')) {
      const prefix = pattern.slice(0, -2);
      return actual.startsWith(`${prefix}.`);
    }

    return false;
  }

  get subscriptions(): ReadonlyArray<ISubscription> {
    return this._subscriptions;
  }

  clear(): void {
    this.subscribers.clear();
    this._subscriptions = [];
  }
}
